import { useState, type ComponentType, type SVGProps } from "react";
import {
  Check,
  Gamepad2,
  Globe2,
  MonitorSmartphone,
  Music2,
  Play,
  Route,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { BrandAppIcon } from "./icons/BrandIcons";
import { useI18n } from "../i18n";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

type Capability = {
  id: "video" | "music" | "game" | "routing" | "devices";
  label: string;
  title: string;
  description: string;
  points: readonly string[];
  icon: Icon;
};

type AppRow = {
  name: string;
  detail: string;
  tone: string;
  serviceId?: string;
};

const capabilities: readonly Capability[] = [
  {
    id: "video",
    label: "国内影音",
    title: "喜欢的节目，不再受距离限制",
    description: "针对常用国内视频平台匹配回国线路，减少等待、卡顿和反复切换节点。",
    points: ["覆盖常用国内影音平台", "根据网络状态自动选择线路", "高清视频播放更连贯"],
    icon: Play,
  },
  {
    id: "music",
    label: "音乐畅听",
    title: "歌单、电台和播客随时继续",
    description: "只处理需要回国访问的音乐流量，日常浏览和工作连接保持原有路径。",
    points: ["支持主流国内音乐服务", "后台自动保持连接", "无需频繁切换网络模式"],
    icon: Music2,
  },
  {
    id: "game",
    label: "国服游戏",
    title: "稳定连接熟悉的国服世界",
    description: "面向国服连接优化传输路径，降低跨境网络带来的延迟波动。",
    points: ["按游戏场景选择线路", "实时显示延迟状态", "异常后自动尝试恢复"],
    icon: Gamepad2,
  },
  {
    id: "routing",
    label: "智能分流",
    title: "国内应用加速，海外应用不绕路",
    description: "云流自动识别目标应用，仅将需要回国的流量送入加速线路。",
    points: ["国内应用走回国线路", "海外服务保持本地直连", "智能模式与手动模式可切换"],
    icon: Route,
  },
  {
    id: "devices",
    label: "多端同步",
    title: "一个账户，连接常用设备",
    description: "电脑和手机共享账户、套餐与连接状态，换设备无需重新配置。",
    points: ["覆盖桌面与移动平台", "套餐权益保持同步", "统一查看设备状态"],
    icon: MonitorSmartphone,
  },
] as const;

const appRows: Record<Capability["id"], readonly AppRow[]> = {
  video: [
    { name: "腾讯视频", detail: "影音线路", tone: "orange", serviceId: "tencent-video" },
    { name: "爱奇艺", detail: "自动选择", tone: "green", serviceId: "iqiyi" },
    { name: "哔哩哔哩", detail: "影音线路", tone: "pink", serviceId: "bilibili" },
  ],
  music: [
    { name: "QQ 音乐", detail: "音乐线路", tone: "green", serviceId: "qq-music" },
    { name: "网易云音乐", detail: "自动选择", tone: "red", serviceId: "netease-music" },
    { name: "播客与电台", detail: "音乐线路", tone: "purple" },
  ],
  game: [
    { name: "国服游戏", detail: "低延迟线路", tone: "orange", serviceId: "cn-games" },
    { name: "语音服务", detail: "本地直连", tone: "blue" },
    { name: "游戏更新", detail: "智能分流", tone: "green" },
  ],
  routing: [
    { name: "国内影音", detail: "回国加速", tone: "orange" },
    { name: "海外网站", detail: "本地直连", tone: "blue" },
    { name: "视频会议", detail: "本地直连", tone: "green" },
  ],
  devices: [
    { name: "Windows", detail: "当前设备", tone: "blue" },
    { name: "macOS", detail: "账户已同步", tone: "purple" },
    { name: "移动设备", detail: "等待连接", tone: "green" },
  ],
} as const;

function ProductWindow({ mode }: { mode: Capability["id"] }) {
  const rows = appRows[mode];
  const { t } = useI18n();

  return (
    <div className="nb-product-window" aria-label={t("云流客户端产品界面预览")}>
      <div className="nb-window-topbar">
        <span className="nb-window-brand"><i />云流</span>
        <span className="nb-window-page-title">{t("连接总览")}</span>
        <div className="nb-window-toolbar"><span>{t("智能模式")}</span><div className="nb-window-controls"><i /><i /><i /></div></div>
      </div>
      <div className="nb-window-body">
        <aside className="nb-window-sidebar" aria-hidden="true">
          <span className="active"><Zap /></span>
          <span><Globe2 /></span>
          <span><MonitorSmartphone /></span>
          <span><ShieldCheck /></span>
        </aside>
        <div className="nb-window-main">
          <div className="nb-connection-head">
            <div>
              <span className="nb-window-kicker">{t("智能加速")}</span>
              <h3>{t("已连接至中国线路")}</h3>
            </div>
            <span className="nb-live-badge"><i />{t("连接正常")}</span>
          </div>
          <div className="nb-route-map" aria-hidden="true">
            <div className="nb-map-grid" />
            <div className="nb-route-origin"><Globe2 /><span>{t("你的位置")}</span></div>
            <span className="nb-route-line route-a" />
            <span className="nb-route-line route-b" />
            <span className="nb-route-pulse" />
            <div className="nb-route-target"><span>CN</span><small>{t("高速线路")}</small></div>
          </div>
          <div className="nb-window-apps">
            {rows.map((row) => (
              <div className="nb-window-app" key={row.name}>
                <span className={`nb-app-dot ${row.tone}`}>
                  {row.serviceId ? <BrandAppIcon serviceId={row.serviceId} size={22} /> : t(row.name).slice(0, 1)}
                </span>
                <div><strong>{t(row.name)}</strong><small>{t(row.detail)}</small></div>
                <span className="nb-app-state"><Check />{t("已就绪")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductShowcase() {
  const [activeId, setActiveId] = useState<Capability["id"]>("video");
  const active = capabilities.find((item) => item.id === activeId) ?? capabilities[0];
  const { t } = useI18n();

  return (
    <section className="nb-capabilities section" aria-labelledby="capability-heading">
      <div className="container">
        <div className="nb-capability-heading">
          <h2 id="capability-heading">{t("一次连接，覆盖常用回国场景")}</h2>
          <p>{t("选择场景，云流自动处理线路和分流规则。")}</p>
        </div>
        <div className="nb-capability-tabs" role="tablist" aria-label={t("产品能力")}>
          {capabilities.map(({ id, label, icon: CapabilityIcon }) => (
            <button
              className={id === activeId ? "active" : ""}
              type="button"
              role="tab"
              aria-selected={id === activeId}
              key={id}
              onClick={() => setActiveId(id)}
            >
              <CapabilityIcon />
              <span>{t(label)}</span>
            </button>
          ))}
        </div>
        <div className="nb-capability-panel" role="tabpanel">
          <div className="nb-capability-copy">
            <h3>{t(active.title)}</h3>
            <p>{t(active.description)}</p>
            <ul>
              {active.points.map((point) => <li key={point}><Check />{t(point)}</li>)}
            </ul>
          </div>
          <ProductWindow mode={active.id} />
        </div>
      </div>
    </section>
  );
}

export function SmartRoutingComparison() {
  const [smart, setSmart] = useState(true);
  const { t } = useI18n();

  return (
    <section className="nb-comparison section" aria-labelledby="comparison-heading">
      <div className="container nb-comparison-grid">
        <div className="nb-comparison-copy">
          <h2 id="comparison-heading">{t("需要加速的才加速")}</h2>
          <p>{t("开启智能分流后，国内内容进入云流线路；海外网站、工作软件和视频会议继续使用本地网络。")}</p>
          <div className="nb-mode-switch" aria-label={t("线路模式")}>
            <button className={!smart ? "active" : ""} type="button" onClick={() => setSmart(false)}>{t("普通 VPN")}</button>
            <button className={smart ? "active" : ""} type="button" onClick={() => setSmart(true)}>{t("云流智能分流")}</button>
          </div>
        </div>
        <div className={`nb-flow-card ${smart ? "is-smart" : "is-legacy"}`}>
          <div className="nb-flow-head">
            <span><i />{t("线路状态")}</span>
            <strong>{t(smart ? "智能模式已开启" : "全局代理模式")}</strong>
          </div>
          <div className="nb-flow-diagram">
            <div className="nb-flow-source"><MonitorSmartphone /><span>{t("你的设备")}</span></div>
            <div className="nb-flow-lines" aria-hidden="true"><i /><i /><i /></div>
            <div className="nb-flow-targets">
              <div><span>{t("国内影音")}</span><strong>{t(smart ? "回国加速" : "代理线路")}</strong></div>
              <div><span>{t("海外网站")}</span><strong>{t(smart ? "本地直连" : "代理线路")}</strong></div>
              <div><span>{t("视频会议")}</span><strong>{t(smart ? "本地直连" : "代理线路")}</strong></div>
            </div>
          </div>
          <div className="nb-flow-summary">
            <Zap />
            <div><strong>{t(smart ? "更短的连接路径" : "所有流量统一绕行")}</strong><span>{t(smart ? "减少不必要的线路占用" : "海外服务也会进入代理线路")}</span></div>
          </div>
        </div>
      </div>
    </section>
  );
}
